/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal
 */

import React, { useState, useEffect, useRef, useMemo } from "react"
import * as localForage from "localforage"
import { useLocation } from "react-router"
import { useHistory } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { WrappedConvertChurnedDialog } from "../WrappedConvertChurnedDialog"
import {
    trackEvent,
    isSmallMobileScreen,
    sharedMomentToCZ,
    getProperty,
    Color,
    Column,
    Label,
    Element,
    MaxLoopOverlay,
    isLandscape,
} from "trux"
import {
    useVideoContainerRef,
    useVideoReady,
    getCameras,
} from "trux/dist/organisms/VideoPlayer"
import Feedback from "../Feedback"
import * as links from "../../util/links"
import { nativeShareMoment } from "../ShareDialog"
import { Page } from "../Navigation"
import {
    useGetMomentCollectionInfoQuery,
    useContentAccessForTeamLazyQuery,
} from "../../generated/graphql"

import doubleTapImg from "./assets/double-tap.svg"
import { isMobile } from "../../util/helpers"
import { GamePageRoutes } from "./GamePageRoutes"

import "./GamePage.css"
import "./vertical.css"
import { GameVideo } from "./GameVideo"

// Game Page

export const GamePage = (props) => {
    //// Collect Data
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const playlistId = props.match.params.playlist_id
    const gameId = props.match.params.game_id
    const fullEventNumber = props.match.params.event_number
    const teamName = props.match.params.team_name
    const extras = links.getUrlExtras(window.location.hash)
    if (extras.vp) {
        let vp = extras.vp + ".json"
        if (vp !== window._trcPanningFile) {
            console.log(
                "GamePage panning file changed from",
                window._trcPanningFile,
                "to",
                vp
            )
            window._trcPanningFile = vp
            document.dispatchEvent(new Event("trc-panning-file-changed"))
        }
    }

    const eventNumberParts = fullEventNumber.split(":")
    const eventNumber = eventNumberParts[0]
    let urlCamera = "tracecam"
    if (eventNumberParts.length > 1) {
        urlCamera = eventNumberParts[1]
    }

    const [verticalVideoMode, setVerticalVideoMode] = useState(false)
    const [verticalReady, setVerticalReady] = useState(false)
    const [showCinnamonToast, setShowCinnamonToast] = useState(false)

    const [maxLoopCount, setMaxLoopCount] = useState(7)

    // META Video Infos
    const containerRef = useVideoContainerRef()
    const isVideoReady = useVideoReady(containerRef)

    const [videoPlayerActive, setVideoPlayerActive] = useState(false)
    const [loopCount, setLoopCount] = useState(0)
    const [showMulticamOnboarding, setShowMulticamOnboarding] = useState(false)
    const [showFluidPlayerOnboarding, setShowFluidPlayerOnboarding] =
        useState(false)
    const [showMulticamTooltip, setShowMulticamTooltip] = useState(false)
    const [showChurnedDialog, setShowChurnedDialog] = useState(false)
    const [forceActive, setForceActive] = useState(false)

    const game = useSelector((state) => {
        return state.game.value
    })

    const team = useSelector((state) => {
        return state.team.value
    })

    const user = useSelector((state) => {
        return state.user.value
    })

    const playlist = useSelector((state) => {
        return state.video.value.playlist
    })

    const event = useSelector((state) => {
        return state.video.value.event
    })

    const currentSource = useSelector((state) => {
        return state.video.value.source
    })

    // camera chosen in events panel filter
    const cameraFilter = useSelector((state) => {
        return state.selectedCamera
    })
    const cameraFilterRef = useRef(cameraFilter)

    // camera that the next moment should default to
    const videoPlayerDefaultCamera = urlCamera

    const activeCamera = useSelector((state) => {
        return state.video.value.camera
    })

    const { acl, enableAddTags } = useSelector((state) => {
        let acl = state.acl.value
        let createEnabled = state.acl.create_moment_tool
        let editEnabled =
            getProperty("video.value.event.meta.type", state, "touch") !==
            "FullGameVideo"
        let enableAddTags = createEnabled && editEnabled
        return {
            acl,
            createEnabled,
            editEnabled,
            enableAddTags,
        }
    })

    const cameras = useMemo(() => {
        let cams = getCameras(game ? game.events : [])
        // we always need at least one camera
        if (cams.length === 0) {
            cams.push("tracecam")
        }
        return cams
    }, [game])

    // redirects family games to the chooser if they land on a tactical playlist
    useEffect(() => {
        const isFamily = team ? team.division?.type === "family" : false
        const allPlaylistOverride = true
        let disabledFamilyOptions = [
            "goals",
            "progression",
            "def-breakdown",
            "touch-chains",
            "attacking",
            "middle",
            "defensive",
        ]

        if (
            !allPlaylistOverride &&
            disabledFamilyOptions.includes(playlistId) &&
            isFamily
        ) {
            history.replace(
                `/${teamName}/games/${gameId}/full-game/first/choose`
            )
        }
    }, [playlistId, team])

    //// Handlers

    const onShareMoment = (e) => {
        const isMulticam = cameras && cameras.length > 1
        if (isMulticam) {
            let camera = activeCamera
                ? activeCamera.charAt(0).toUpperCase() + activeCamera.slice(1)
                : "Multicam"
            if (camera.includes("Phone")) {
                camera = camera.split("-")[0]
            }
            const eventAction = `Share ${camera} Moment`
            trackEvent({
                action: eventAction,
                label: "Share Moment",
                element: e ? e.target : null,
            })
        }
        const isFamily = team ? team.division?.type === "family" : false
        if (isFamily) {
            const eventAction = "Share Family Moment"
            trackEvent({
                action: eventAction,
                label: "Share Moment",
                element: e ? e.target : null,
            })
        }
        let ok = nativeShareMoment(game, playlist, event)
        if (ok) {
            trackEvent({
                action: "Native Share Pressed",
                label: "Share Button",
                element: document.querySelector(".VideoControls *"),
            })
            sharedMomentToCZ({
                type: "native share",
                user_id: user?.user_id,
                where: "main",
                moment_id: event?.game_clip_id,
            })
            e.preventDefault()
        } else {
            props.history.push(
                links.gameShareUrl(
                    teamName,
                    playlistId,
                    gameId,
                    fullEventNumber
                )
            )
        }
    }

    //// Onboarding Messages / Dialogs / Tooltips / etc ////

    const fluidPlayerOnboardingKey = "fluid_player_onboarding"
    const multicamOnboardingKey = game
        ? `multicam_onboarding_shown_${game.game_id}`
        : null
    const multicamTooktipKey = game
        ? `multicam_tooltip_shown_${game.game_id}`
        : null

    useEffect(() => {
        // Each time the game changes, we want to determine whether or not
        // to show the multicam onboarding dialog.

        const shouldShowFluidPlayerOnboarding = async () => {
            const alreadyShown = await localForage.getItem(
                fluidPlayerOnboardingKey
            )
            const shouldShow = alreadyShown || isMobile() ? false : true
            return shouldShow
        }

        const shouldShowMulticamOnboarding = async (game) => {
            const alreadyShown = await localForage.getItem(
                multicamOnboardingKey
            )
            const shouldShow = alreadyShown ? false : true
            return shouldShow
        }

        const check = async () => {
            let showFluidPlayerOnboarding =
                await shouldShowFluidPlayerOnboarding()
            if (showFluidPlayerOnboarding) {
                setShowFluidPlayerOnboarding(showFluidPlayerOnboarding)
                localForage.setItem(fluidPlayerOnboardingKey, true)
            }
            const isMulticam = cameras && cameras.length > 1
            if (
                game &&
                isMulticam &&
                multicamOnboardingKey &&
                !showFluidPlayerOnboarding
            ) {
                let shouldShow = await shouldShowMulticamOnboarding(game)
                if (shouldShow) {
                    setShowMulticamOnboarding(shouldShow)
                    localForage.setItem(multicamOnboardingKey, true)
                }
            }
        }
        if (game) {
            check()
        }
    }, [game])

    useEffect(() => {
        // Each time the event/source changes, we want to determine whether or not
        // to show the multicam tooltip.
        const shouldShowMulticamTooltip = async (game) => {
            const alreadyShown = await localForage.getItem(multicamTooktipKey)
            const shouldShow = alreadyShown ? false : true
            return shouldShow
        }

        const shouldShow = event && game
        if (shouldShow) {
            const isGoalOrHit = event.tags?.some(
                (el) =>
                    el.name === "goal" ||
                    el.name === "Goal" ||
                    el.name === "hit"
            )
            const isMulticamGoal = event.sources && event.sources.length > 0
            if (isGoalOrHit && isMulticamGoal && multicamTooktipKey) {
                shouldShowMulticamTooltip(game).then((shouldShow) => {
                    if (shouldShow) {
                        setShowMulticamTooltip(shouldShow)
                        localForage.setItem(multicamTooktipKey, true)
                    }
                })
            }
        }

        return () => {
            setShowMulticamTooltip(false)
        }
    }, [event, game])

    //// End Onboarding Messages / Dialogs / Tooltips / etc ////

    const toast = (msg) => {
        //console.log("toast", msg)
        dispatch({
            type: "COMMON_SET",
            key: "toast",
            value: msg,
        })
    }

    const maxLoopPlayPressed = () => {
        setMaxLoopCount(maxLoopCount * 3)
    }

    const openMomentsSelector = () => {
        // console.log(
        //     "openMomentsSelector",
        //     links.gameNotesUrl(teamName, playlistId, gameId, fullEventNumber)
        // )
        props.history.push(
            links.gameNotesUrl(teamName, playlistId, gameId, fullEventNumber)
        )
    }

    const gotoPlayerPage = (player) => {
        if (player) {
            props.history.push(
                links.gamePlayerDetailsUrl(
                    teamName,
                    playlistId,
                    gameId,
                    fullEventNumber,
                    player.trace_number
                )
            )
        }
    }

    useEffect(() => {
        // Grab the start time from the URL if we need to & have it
        // Determine whether we should force the active state
        const forceActiveVideoPlayerState =
            location.pathname.includes("create-moment") ||
            location.pathname.includes("save-moment")
        if (forceActive !== forceActiveVideoPlayerState) {
            setForceActive(forceActiveVideoPlayerState)
        }
    }, [location])

    useEffect(() => {
        document.body.className += " game"
        return () => {
            document.body.className = document.body.className.replace(
                " game",
                ""
            )
        }
    }, [])

    useEffect(() => {
        const onSourceChange = (e) => {
            let cam =
                e.detail === "all" || e.detail === "tracecam"
                    ? ""
                    : `:${e.detail}`
            let path = props.history.location.pathname
            let parts = path.split("/")
            if (parts.length > 5) {
                if (cam) {
                    parts[5] = `${eventNumber}${cam}`
                } else {
                    parts[5] = `${eventNumber}`
                }
                let path = parts.join("/")
                if (props.history.location.pathname !== path) {
                    console.log("GamePage onSourceChange", path, cam)
                    props.history.push(path)
                }
            }
        }
        document.addEventListener("trc-source-changed", onSourceChange)
        return () => {
            document.removeEventListener("trc-source-changed", onSourceChange)
        }
    }, [eventNumber, cameraFilter])

    useEffect(() => {
        let qa = window.location.hash.includes("/qa/")
        let effectiveCameraFilter = cameraFilter === "all" ? "" : cameraFilter
        if (
            !qa &&
            effectiveCameraFilter !== urlCamera &&
            cameraFilter !== cameraFilterRef.current
        ) {
            cameraFilterRef.current = cameraFilter
            let cam =
                cameraFilter === "all" || cameraFilter === "tracecam"
                    ? ""
                    : `:${cameraFilter}`
            let path = props.history.location.pathname
            let parts = path.split("/")
            if (parts.length > 5) {
                if (cam) {
                    parts[5] = `${eventNumber}${cam}`
                } else {
                    parts[5] = `${eventNumber}`
                }
                let path = parts.join("/")
                if (props.history.location.pathname !== path) {
                    //console.log("useEffect camera change", path)
                    props.history.push(path)
                }
            }
        } else if (!qa && effectiveCameraFilter !== cameraFilter) {
            console.log("forcing url camera", urlCamera)
            //selectCamera(urlCamera)
        }
    }, [cameraFilter, cameraFilterRef])

    const { data: momentCollectionData } = useGetMomentCollectionInfoQuery({
        variables: {
            token: props.token,
            game_clip_id: event?.game_clip_id,
            user_id: props.token?.user_id,
        },
        skip: !props.token !== null || !event?.game_clip_id || !isVideoReady,
    })

    // Let's replace the event with the currentSource if we need to
    let currentEvent = event ? { ...event } : null
    if (currentSource && event) {
        let matchingEvent = event.sources?.find((source) => {
            return (
                parseInt(source.game_clip_id) ===
                parseInt(currentSource.game_clip_id)
            )
        })
        if (matchingEvent) {
            currentEvent = { ...matchingEvent }
            currentEvent.full_game_time = event.full_game_time
        }
    }

    // If we're viewing a different source than default, let's copy that source's tags
    // onto the moment b/c MomentRow uses the tags on the moment itself to generate
    // tags rather than the tags prop
    const tags = currentEvent ? currentEvent.tags : event ? event.tags : []
    if (currentEvent !== event) {
        currentEvent.tags = tags
        currentEvent.display_number = event.display_number
    }

    const [fetchContentAccess] = useContentAccessForTeamLazyQuery({})

    useEffect(() => {
        // Get the content access
        if (team && props.token) {
            fetchContentAccess({
                variables: {
                    team_id: team?.team_id,
                    game_id: game?.game_id,
                    token: props.token,
                },
                skip: !props.token || !team || !game || !isVideoReady,
            }).then((contentAccess) => {
                const access = contentAccess?.data?.contentAccessForTeam
                const allowed = access ? access.allowed : true
                const shouldShow =
                    !allowed && !window.location.href?.includes("success")
                setShowChurnedDialog(shouldShow)
            })
        }
    }, [user, props.token, team, isVideoReady])

    /**** Video Click and Toast Stuff *****/
    const videoClickTimeout = useRef(null)
    const cinnamonToastTimeout = useRef(null)
    const videoClicks = useRef(0)
    const onVerticalToggle = (e) => {
        if (
            (isMobile() && window.innerHeight > window.innerWidth) ||
            !isMobile()
        ) {
            console.log("GamePage.onVerticalToggle", e)
            clearTimeout(videoClickTimeout.current)
            clearTimeout(cinnamonToastTimeout.current)
            videoClicks.current += 1

            // first click
            if (videoClicks.current === 1) {
                console.log("GamePage first click")
                videoClickTimeout.current = setTimeout(() => {
                    console.log("GamePage show toast")
                    setShowCinnamonToast(true)
                }, 200)
                cinnamonToastTimeout.current = setTimeout(() => {
                    videoClicks.current = 0
                    console.log("GamePage toast timeout")
                    setShowCinnamonToast(false)
                }, 1000)
            }

            // second click
            else if (videoClicks.current > 1) {
                console.log("GamePage second click")
                videoClicks.current = 0
                setShowCinnamonToast(false)
                setVerticalVideoMode(!verticalVideoMode)
            } else {
                console.log("GamePage else", videoClicks.current)
            }
        }
    }

    const onVerticalReady = (e) => {
        if (e.detail === true && isSmallMobileScreen()) {
            console.log("GamePage.onVerticalReady", e.detail)
            setVerticalVideoMode(true)
            setVerticalReady(true)
        }
    }
    const onOrientationChanged = (e) => {
        console.log("GamePage.onOrientationChanged", e)
        if (isLandscape()) {
            setVerticalVideoMode(false)
        } else if (isMobile()) {
            setVerticalVideoMode(verticalReady)
        }
    }

    useEffect(() => {
        // add on orientation change listener
        window.addEventListener("resize", onOrientationChanged)
        document.addEventListener("trc-vertical-panning-tap", onVerticalToggle)
        document.addEventListener(
            "trc-vertical-panning-loaded",
            onVerticalReady
        )
        return () => {
            document.removeEventListener(
                "trc-vertical-panning-tap",
                onVerticalToggle
            )
            document.removeEventListener(
                "trc-vertical-panning-loaded",
                onVerticalReady
            )
            window.removeEventListener("resize", onOrientationChanged)
        }
    }, [videoClicks.current, verticalVideoMode])
    /**** end Video Click and Toast Stuff *****/

    /*** variables needed for JSX  ***/
    let pageClassNames = ["GamePage"]
    if (verticalVideoMode) {
        pageClassNames.push("vertical")
        if (videoPlayerActive) {
            pageClassNames.push("video-active")
        }
    }
    /*** END: variables needed for JSX  ***/

    return (
        <Page
            isGameResults
            noPadding
            skip={!isVideoReady}
            className={pageClassNames.join(" ")}
        >
            <GamePageRoutes
                token={props.token}
                createEnabled={props.createEnabled}
                editEnabled={props.editEnabled}
                containerRef={containerRef}
            />
            <div className="Game">
                {showChurnedDialog && (
                    <WrappedConvertChurnedDialog
                        videoContainerRef={containerRef}
                        hideCloseButton={true}
                    />
                )}
                <div className="GameVideoSection">
                    <GameVideo
                        containerRef={containerRef}
                        teamName={teamName}
                        gameId={gameId}
                        playlistId={playlistId}
                        eventNumber={eventNumber}
                        game={game}
                        playlist={playlist}
                        event={event}
                        currentEvent={currentEvent}
                        extras={extras}
                        cameraFilter={cameraFilter}
                        urlCamera={urlCamera}
                        forceActive={forceActive}
                        showFluidPlayerOnboarding={showFluidPlayerOnboarding}
                        setShowFluidPlayerOnboarding={
                            setShowFluidPlayerOnboarding
                        }
                        showMulticamOnboarding={showMulticamOnboarding}
                        setShowMulticamOnboarding={setShowMulticamOnboarding}
                        showMulticamTooltip={showMulticamTooltip}
                        setShowMulticamTooltip={setShowMulticamTooltip}
                        showChurnedDialog={showChurnedDialog}
                        setGPVideoPlayerActive={setVideoPlayerActive}
                        setGPLoopCount={setLoopCount}
                        enableAddTags={enableAddTags}
                        onShareMoment={onShareMoment}
                        toast={toast}
                        verticalVideoMode={verticalVideoMode}
                        openMomentsSelector={openMomentsSelector}
                        gotoPlayerPage={gotoPlayerPage}
                        fullEventNumber={fullEventNumber}
                        cameras={cameras}
                        videoPlayerDefaultCamera={videoPlayerDefaultCamera}
                        user={user}
                    />

                    {showCinnamonToast && (
                        <Element className="cinnamon-toast">
                            <Element className="cinnamon-toast-window">
                                <Column span="12" center>
                                    <Element
                                        tag="img"
                                        src={doubleTapImg}
                                        marginBottom="2"
                                        height="18px"
                                        width="18px"
                                    />
                                    {verticalVideoMode && (
                                        <Label bold color={Color.White2}>
                                            Double-tap&nbsp;for&nbsp;small&nbsp;video
                                        </Label>
                                    )}
                                    {!verticalVideoMode && (
                                        <Label bold color={Color.White2}>
                                            Double-tap&nbsp;for&nbsp;vertical&nbsp;video
                                        </Label>
                                    )}
                                </Column>
                            </Element>
                        </Element>
                    )}

                    <Feedback
                        videoContainerRef={containerRef}
                        extras={extras}
                        toast={toast}
                        game={game}
                        event={event}
                        user={user}
                    />

                    <MaxLoopOverlay
                        event={event}
                        videoContainerRef={containerRef}
                        maxLoopCount={maxLoopCount}
                        loopCount={loopCount}
                        showOverlay={false}
                        playPressed={maxLoopPlayPressed}
                        sharePressed={onShareMoment}
                        acl={acl}
                        saveUrl={links
                            .gameShareUrl(
                                teamName,
                                playlistId,
                                gameId,
                                fullEventNumber
                            )
                            .replace("share", "save/profile")}
                        momentCollectionData={momentCollectionData}
                    />
                </div>
            </div>
        </Page>
    )
}
